import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { consultationService } from '../services/consultationApi';
import AuroraLoadingOverlay from '../components/AuroraLoadingOverlay';
import NoTokensBanner from '../components/NoTokensBanner';
import { renderMarkdown } from '../utils/renderMarkdown';

const MAX_CHARS = 3000;

const AuroraQuestionPage: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!question.trim() || isLoading) return;
    setIsLoading(true);
    setError(null);
    setAnswer('');
    try {
      const result = await consultationService.askAurora(question.trim());
      setAnswer(result.general_interpretation);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Erro ao consultar Aurora Lyz');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNew = () => {
    setQuestion('');
    setAnswer('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Aurora Lyz</h1>
        <div className="w-20" />
      </div>
      <AuroraLoadingOverlay isLoading={isLoading} message="Aurora está respondendo..." />

      <div className="container mx-auto px-4 py-5 sm:py-10 max-w-2xl">

        {/* Intro */}
        <div className="text-center mb-8">
          <div className="text-3xl sm:text-5xl mb-3">🔮</div>
          <h2 className="text-lg sm:text-2xl font-mystical font-bold mb-2">Pergunte à Aurora Lyz</h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Faça qualquer pergunta — tarot, astrologia, numerologia, espiritualidade — e Aurora responde com sabedoria e contexto personalizado.
          </p>
          <p className="text-amber-400/80 text-xs mt-2">Consome 1 token por pergunta</p>
        </div>

        <AnimatePresence mode="wait">
          {/* Formulário */}
          {!answer && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className="relative mb-4">
                <textarea
                  value={question}
                  onChange={e => setQuestion(e.target.value.slice(0, MAX_CHARS))}
                  placeholder="Ex: O que os astros dizem sobre minha carreira este mês? / Qual é o significado do número 11 na minha vida? / Como liberar o medo de recomeçar?"
                  rows={5}
                  className="w-full border rounded-2xl px-5 py-4 focus:outline-none resize-none text-sm leading-relaxed"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    caretColor: 'white',
                  }}
                  disabled={isLoading}
                />
                <span className="absolute bottom-3 right-4 text-white/30 text-xs">
                  {question.length}/{MAX_CHARS}
                </span>
              </div>

              <NoTokensBanner error={error} />

              <motion.button
                onClick={handleSubmit}
                disabled={!question.trim() || isLoading}
                whileHover={!isLoading && question.trim() ? { scale: 1.03 } : {}}
                whileTap={!isLoading && question.trim() ? { scale: 0.97 } : {}}
                className="w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)' }}
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Aurora está respondendo…
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    Perguntar à Aurora Lyz
                  </>
                )}
              </motion.button>
            </motion.div>
          )}

          {/* Resposta */}
          {answer && (
            <motion.div
              key="answer"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Pergunta original */}
              <div className="mb-4 px-4 py-3 rounded-xl bg-white/6 border border-white/15">
                <p className="text-white/50 text-xs mb-1">Sua pergunta</p>
                <p className="text-white/80 text-sm italic">"{question}"</p>
              </div>

              {/* Resposta da Aurora */}
              <div
                className="rounded-2xl p-4 sm:p-6 mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(217,119,6,0.15) 0%, rgba(124,58,237,0.15) 100%)',
                  border: '1px solid rgba(217,119,6,0.3)',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">🔮</span>
                  <span className="text-amber-400 font-mystical font-semibold text-sm">Aurora Lyz responde</span>
                </div>
                <div className="text-white/90 text-sm leading-relaxed text-justify">{renderMarkdown(answer)}</div>
              </div>

              <button
                onClick={handleNew}
                className="w-full py-3 rounded-2xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all text-sm font-medium"
              >
                Fazer outra pergunta
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AuroraQuestionPage;

